/**
 * Server functions voor de module Realisaties.
 * Publiek lezen gebeurt met de publishable key: RLS toont enkel gepubliceerde realisaties.
 * Beheeracties draaien als de ingelogde gebruiker; RLS bewaakt can_write en can_publish.
 */
import { valideerWaarde } from "@/lib/rich-text";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database, Json } from "@/integrations/supabase/types";
import type { MediaAsset, MediaVariant } from "@/lib/media.functions";
import { readFocal } from "@/lib/media-taxonomy";
import { deliverMediaAssets, localizeMediaAssets } from "@/lib/media-delivery.server";

type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type MediaRow = Database["public"]["Tables"]["media_assets"]["Row"];

export type ProjectMediaRole = "hoofdbeeld" | "gewoon" | "voor" | "na";

export type ProjectPhoto = {
  id: string;
  mediaId: string;
  role: ProjectMediaRole;
  sortOrder: number;
  /** Bij een voor-na-paar: het id van het bijbehorende beeld. */
  pairedMediaId: string | null;
  media: MediaAsset;
};

export type ProjectQuote = {
  id: string;
  citaat: string;
  naam: string;
  gemeente: string;
  toestemming: boolean;
  toestemmingOp: string | null;
  zichtbaar: boolean;
};

export type ProjectFaq = { vraag: string; antwoord: string };

export type Project = {
  id: string;
  slug: string;
  titel: string;
  typeWerk: string;
  gemeente: string;
  provincie: string;
  uitvoeringsjaar: number | null;
  afmetingen: string;
  uitgevoerdeWerken: string[];
  gebruikteMaterialen: string[];
  partners: string[];
  samenvatting: string;
  beschrijving: string;
  antwoordSamenvatting: string;
  status: "concept" | "gepubliceerd";
  uitgelicht: boolean;
  sortOrder: number;
  metaTitle: string;
  metaDescription: string;
  faqs: ProjectFaq[];
  lang: string;
  updatedAt: string;
};

export type ProjectDetail = Project & { photos: ProjectPhoto[]; quote: ProjectQuote | null };

export type PublishedMunicipality = {
  slug: string;
  gemeente: string;
  provincie: string;
  projectCount: number;
  lastmod: string;
};

const PROJECT_COLUMNS =
  "id, slug, titel, type_werk, gemeente, provincie, uitvoeringsjaar, afmetingen, uitgevoerde_werken, gebruikte_materialen, partners, samenvatting, beschrijving, antwoord_samenvatting, status, uitgelicht, sort_order, meta_title, meta_description, faqs, lang, updated_at";

const MEDIA_COLUMNS =
  "id, collection, phase, path, url, filename, alt, caption, width, height, size_bytes, sort_order, variants, decorative, focal_x, focal_y, placeholder_color";

/** Veelgestelde vragen staan als JSON op de realisatie; onbruikbare rijen vallen weg. */
function readFaqs(value: unknown): ProjectFaq[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({ vraag: String(item["vraag"] ?? ""), antwoord: String(item["antwoord"] ?? "") }))
    .filter((faq) => faq.vraag.trim() !== "" && faq.antwoord.trim() !== "");
}

function mapProject(row: ProjectRow): Project {
  return {
    id: row.id,
    slug: row.slug,
    titel: row.titel,
    typeWerk: row.type_werk,
    gemeente: row.gemeente,
    provincie: row.provincie,
    uitvoeringsjaar: row.uitvoeringsjaar,
    afmetingen: row.afmetingen,
    uitgevoerdeWerken: row.uitgevoerde_werken ?? [],
    gebruikteMaterialen: row.gebruikte_materialen ?? [],
    partners: row.partners ?? [],
    samenvatting: row.samenvatting,
    beschrijving: row.beschrijving,
    antwoordSamenvatting: row.antwoord_samenvatting,
    status: row.status === "gepubliceerd" ? "gepubliceerd" : "concept",
    uitgelicht: row.uitgelicht,
    sortOrder: row.sort_order,
    metaTitle: row.meta_title,
    metaDescription: row.meta_description,
    faqs: readFaqs(row.faqs),
    lang: row.lang,
    updatedAt: row.updated_at,
  };
}

function readVariants(value: unknown): MediaVariant[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is MediaVariant =>
      typeof item === "object" && item !== null && typeof (item as MediaVariant).url === "string",
  );
}

function mapMedia(row: MediaRow): MediaAsset {
  return {
    id: row.id,
    collection: row.collection,
    phase: row.phase,
    path: row.path,
    url: row.url,
    filename: row.filename,
    alt: row.alt,
    caption: row.caption ?? "",
    width: row.width,
    height: row.height,
    sizeBytes: row.size_bytes,
    sortOrder: row.sort_order,
    variants: readVariants(row.variants),
    // Het origineel kan GPS-gegevens bevatten en hoort niet in publieke antwoorden.
    originalUrl: null,
    // Cloudflare wordt daarna toegepast via deliverMediaAssets, met de eigen opslag als terugval.
    cfImageId: null,
    source: "supabase",
    fallbackUrl: null,
    focal: readFocal(row.focal_x, row.focal_y),
    decorative: row.decorative,
    placeholderColor: row.placeholder_color,
  };
}

type MediaJoinRow = {
  id: string;
  media_id: string;
  rol: string;
  sort_order: number;
  gekoppeld_media_id: string | null;
  media_assets: MediaRow | null;
};

function mapPhotos(rows: MediaJoinRow[]): ProjectPhoto[] {
  return rows
    .filter((row) => row.media_assets !== null)
    .map((row) => ({
      id: row.id,
      mediaId: row.media_id,
      role: (["hoofdbeeld", "gewoon", "voor", "na"].includes(row.rol) ? row.rol : "gewoon") as ProjectMediaRole,
      sortOrder: row.sort_order,
      pairedMediaId: row.gekoppeld_media_id,
      media: mapMedia(row.media_assets!),
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

type SupabaseClientLike = Parameters<typeof deliverMediaAssets>[0];

/** Past de leverwijze (Cloudflare of eigen opslag) toe op de foto's van realisaties. */
async function withDelivery(client: SupabaseClientLike, photos: ProjectPhoto[]) {
  const delivered = await deliverMediaAssets(
    client,
    photos.map((photo) => photo.media),
  );
  return photos.map((photo, index) => ({ ...photo, media: delivered[index] ?? photo.media }));
}

/** Zet beschrijving en bijschrift van de foto's in de taal van de bezoeker, met terugval op het Nederlands. */
async function withPhotoLanguage(client: SupabaseClientLike, photos: ProjectPhoto[], lang: string) {
  const localized = await localizeMediaAssets(
    client,
    photos.map((photo) => photo.media),
    lang,
  );
  return photos.map((photo, index) => ({ ...photo, media: localized[index] ?? photo.media }));
}

/** Publieke client zonder sessie; opaque sb_-keys horen enkel in de apikey-header. */
function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export type ProjectCard = Project & { hoofdbeeld: MediaAsset | null };

/**
 * Vertalingen van realisaties (fase H2). Een leeg veld valt terug op het Nederlands.
 * Zolang er geen vertaalde rij is, blijft alles Nederlands.
 */
type TranslationRow = Database["public"]["Tables"]["project_translations"]["Row"];
type SupabaseLikeClient = ReturnType<typeof createClient<Database>>;

async function applyTranslations<T extends Project>(
  client: SupabaseLikeClient,
  projects: T[],
  lang: string,
): Promise<T[]> {
  if (lang === "nl" || projects.length === 0) return projects;
  const { data } = await client
    .from("project_translations")
    .select("project_id, titel, samenvatting, beschrijving, antwoord_samenvatting, meta_title, meta_description, faqs")
    .eq("lang", lang)
    .in(
      "project_id",
      projects.map((project) => project.id),
    );
  const rows = (data ?? []) as Pick<
    TranslationRow,
    | "project_id"
    | "titel"
    | "samenvatting"
    | "beschrijving"
    | "antwoord_samenvatting"
    | "meta_title"
    | "meta_description"
    | "faqs"
  >[];
  const map = new Map(rows.map((row) => [row.project_id, row]));

  const kies = (vertaald: string | null | undefined, bron: string) =>
    vertaald && vertaald.trim() !== "" ? vertaald : bron;
  return projects.map((project) => {
    const vertaling = map.get(project.id);
    if (!vertaling) return project;
    const faqs = readFaqs(vertaling.faqs);
    return {
      ...project,
      titel: kies(vertaling.titel, project.titel),
      samenvatting: kies(vertaling.samenvatting, project.samenvatting),
      beschrijving: kies(vertaling.beschrijving, project.beschrijving),
      antwoordSamenvatting: kies(vertaling.antwoord_samenvatting, project.antwoordSamenvatting),
      metaTitle: kies(vertaling.meta_title, project.metaTitle),
      metaDescription: kies(vertaling.meta_description, project.metaDescription),
      faqs: faqs.length > 0 ? faqs : project.faqs,
    };
  });
}

/** Alle gepubliceerde realisaties met hun hoofdbeeld, voor het overzicht. */
export const listPublishedProjects = createServerFn({ method: "GET" })
  .inputValidator((data?: { lang?: string }) => ({ lang: data?.lang ?? "nl" }))
  .handler(async ({ data }) => {
  const client = publicClient();
  const { data: rows0, error } = await client
    .from("projects")
    .select(PROJECT_COLUMNS)
    .eq("status", "gepubliceerd")
    .order("uitgelicht", { ascending: false })
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  const projects = await applyTranslations(client, (rows0 ?? []).map((row) => mapProject(row as ProjectRow)), data.lang);
  if (projects.length === 0) return [] as ProjectCard[];


  const { data: media, error: mediaError } = await client
    .from("project_media")
    .select(`id, project_id, media_id, rol, sort_order, gekoppeld_media_id, media_assets!project_media_media_id_fkey ( ${MEDIA_COLUMNS} )`)
    .in(
      "project_id",
      projects.map((project) => project.id),
    )
    .order("sort_order", { ascending: true });
  if (mediaError) throw new Error(mediaError.message);

  const rows = (media ?? []) as unknown as (MediaJoinRow & { project_id: string })[];
  const cards = projects.map((project) => {
    const own = rows.filter((row) => row.project_id === project.id && row.media_assets);
    const hero = own.find((row) => row.rol === "hoofdbeeld") ?? own[0];
    return { ...project, hoofdbeeld: hero?.media_assets ? mapMedia(hero.media_assets) : null };
  });
  const heroes = await localizeMediaAssets(
    client,
    await deliverMediaAssets(
      client,
      cards.flatMap((card) => (card.hoofdbeeld ? [card.hoofdbeeld] : [])),
    ),
    data.lang,
  );
  const byId = new Map(heroes.map((hero) => [hero.id, hero]));
  return cards.map((card) => ({
    ...card,
    hoofdbeeld: card.hoofdbeeld ? (byId.get(card.hoofdbeeld.id) ?? card.hoofdbeeld) : null,
  }));
});

/** Gemeenten die de databankdrempel van drie gepubliceerde realisaties halen. */
export const listPublishedMunicipalities = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await publicClient()
    .from("published_project_municipalities")
    .select("slug, gemeente, provincie, project_count, lastmod")
    .order("gemeente");
  if (error) throw new Error(error.message);
  return (data ?? []).flatMap((row) =>
    row.slug && row.gemeente && row.project_count && row.lastmod
      ? [{
          slug: row.slug,
          gemeente: row.gemeente,
          provincie: row.provincie ?? "",
          projectCount: row.project_count,
          lastmod: row.lastmod.slice(0, 10),
        }]
      : [],
  ) satisfies PublishedMunicipality[];
});

/** Eén geldige gemeente en haar gepubliceerde realisaties. */
export const getPublishedMunicipality = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => ({ slug: data.slug.trim().toLowerCase() }))
  .handler(async ({ data }) => {
    const client = publicClient();
    const { data: municipality, error } = await client
      .from("published_project_municipalities")
      .select("slug, gemeente, provincie, project_count, lastmod")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!municipality?.gemeente) return null;

    const { data: rows, error: projectsError } = await client
      .from("projects")
      .select(PROJECT_COLUMNS)
      .eq("status", "gepubliceerd")
      .ilike("gemeente", municipality.gemeente)
      .order("sort_order");
    if (projectsError) throw new Error(projectsError.message);
    return {
      municipality: {
        slug: municipality.slug ?? data.slug,
        gemeente: municipality.gemeente,
        provincie: municipality.provincie ?? "",
        projectCount: municipality.project_count ?? 0,
        lastmod: municipality.lastmod?.slice(0, 10) ?? "",
      } satisfies PublishedMunicipality,
      projects: (rows ?? []).map((row) => mapProject(row as ProjectRow)),
    };
  });

/** Eén gepubliceerde realisatie met foto's en eventuele quote. Null als ze niet bestaat. */
export const getPublishedProject = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string; lang?: string }) => ({ slug: String(data.slug), lang: data.lang ?? "nl" }))
  .handler(async ({ data }): Promise<ProjectDetail | null> => {
    const client = publicClient();
    const { data: row, error } = await client
      .from("projects")
      .select(PROJECT_COLUMNS)
      .eq("slug", data.slug)
      .eq("status", "gepubliceerd")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const [project] = await applyTranslations(client, [mapProject(row as ProjectRow)], data.lang);
    if (!project) return null;


    const [{ data: media }, { data: quote }] = await Promise.all([
      client
        .from("project_media")
        .select(`id, media_id, rol, sort_order, gekoppeld_media_id, media_assets!project_media_media_id_fkey ( ${MEDIA_COLUMNS} )`)
        .eq("project_id", project.id)
        .order("sort_order", { ascending: true }),
      client
        .from("project_quotes")
        .select("id, citaat, naam, gemeente, toestemming, toestemming_op, zichtbaar")
        .eq("project_id", project.id)
        .maybeSingle(),
    ]);

    return {
      ...project,
      photos: await withPhotoLanguage(
        client,
        await withDelivery(client, mapPhotos((media ?? []) as unknown as MediaJoinRow[])),
        data.lang,
      ),
      quote: quote
        ? {
            id: quote.id,
            citaat: quote.citaat,
            naam: quote.naam,
            gemeente: quote.gemeente,
            toestemming: quote.toestemming,
            toestemmingOp: quote.toestemming_op,
            zichtbaar: quote.zichtbaar,
          }
        : null,
    };
  });

/** Alle realisaties voor het beheer, concepten inbegrepen. */
export const adminListProjects = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("projects")
      .select(PROJECT_COLUMNS)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => mapProject(row as ProjectRow));
  });

/** Eén realisatie voor het beheer, met foto's en quote. */
export const adminGetProject = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { slug: string }) => ({ slug: String(data.slug) }))
  .handler(async ({ data, context }): Promise<ProjectDetail | null> => {
    const { data: row, error } = await context.supabase
      .from("projects")
      .select(PROJECT_COLUMNS)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const project = mapProject(row as ProjectRow);

    const [{ data: media }, { data: quote }] = await Promise.all([
      context.supabase
        .from("project_media")
        .select(`id, media_id, rol, sort_order, gekoppeld_media_id, media_assets!project_media_media_id_fkey ( ${MEDIA_COLUMNS} )`)
        .eq("project_id", project.id)
        .order("sort_order", { ascending: true }),
      context.supabase
        .from("project_quotes")
        .select("id, citaat, naam, gemeente, toestemming, toestemming_op, zichtbaar")
        .eq("project_id", project.id)
        .maybeSingle(),
    ]);

    return {
      ...project,
      photos: await withDelivery(
        context.supabase,
        mapPhotos((media ?? []) as unknown as MediaJoinRow[]),
      ),
      quote: quote
        ? {
            id: quote.id,
            citaat: quote.citaat,
            naam: quote.naam,
            gemeente: quote.gemeente,
            toestemming: quote.toestemming,
            toestemmingOp: quote.toestemming_op,
            zichtbaar: quote.zichtbaar,
          }
        : null,
    };
  });

export type ProjectInput = {
  id?: string;
  slug: string;
  titel: string;
  typeWerk: string;
  gemeente: string;
  provincie: string;
  uitvoeringsjaar: number | null;
  afmetingen: string;
  uitgevoerdeWerken: string[];
  gebruikteMaterialen: string[];
  partners: string[];
  samenvatting: string;
  beschrijving: string;
  antwoordSamenvatting: string;
  status: "concept" | "gepubliceerd";
  uitgelicht: boolean;
  sortOrder: number;
  metaTitle: string;
  metaDescription: string;
  faqs: ProjectFaq[];
};

function readableError(message: string) {
  if (message.includes("projects_slug_key") || message.includes("duplicate key")) {
    return "Dit webadres is al in gebruik door een andere realisatie. Kies een ander webadres.";
  }
  if (message.includes("publiceren")) return message;
  if (message.includes("row-level security")) {
    return "U hebt geen recht om realisaties te bewerken. Vraag dit aan een beheerder.";
  }
  return message;
}

/** Realisatie aanmaken of bijwerken. Publiceren wordt in de databank getoetst. */
export const saveProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: ProjectInput) => data)
  .handler(async ({ data, context }) => {
    if (data.titel.trim() === "") throw new Error("Geef de realisatie een titel.");
    const tekstFouten = valideerWaarde(data.beschrijving, "artikel", "Beschrijving");
    if (tekstFouten.length > 0) throw new Error(tekstFouten.join(" "));
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(data.slug)) {
      throw new Error("Het webadres mag enkel kleine letters, cijfers en streepjes bevatten.");
    }

    const payload = {
      slug: data.slug,
      titel: data.titel.trim(),
      type_werk: data.typeWerk,
      gemeente: data.gemeente.trim(),
      provincie: data.provincie.trim(),
      uitvoeringsjaar: data.uitvoeringsjaar,
      afmetingen: data.afmetingen.trim(),
      uitgevoerde_werken: data.uitgevoerdeWerken,
      gebruikte_materialen: data.gebruikteMaterialen,
      partners: data.partners,
      samenvatting: data.samenvatting.trim(),
      beschrijving: data.beschrijving.trim(),
      antwoord_samenvatting: data.antwoordSamenvatting.trim(),
      status: data.status,
      uitgelicht: data.uitgelicht,
      sort_order: data.sortOrder,
      meta_title: data.metaTitle.trim(),
      meta_description: data.metaDescription.trim(),
      faqs: JSON.parse(
        JSON.stringify(
          (data.faqs ?? [])
            .map((faq) => ({ vraag: faq.vraag.trim(), antwoord: faq.antwoord.trim() }))
            .filter((faq) => faq.vraag !== "" && faq.antwoord !== ""),
        ),
      ) as Json,
      updated_by: context.userId,
    };

    if (data.id) {
      const { error } = await context.supabase.from("projects").update(payload).eq("id", data.id);
      if (error) throw new Error(readableError(error.message));
      return { slug: data.slug };
    }

    const { error } = await context.supabase
      .from("projects")
      .insert({ ...payload, created_by: context.userId });
    if (error) throw new Error(readableError(error.message));
    return { slug: data.slug };
  });

/** Realisatie verwijderen (enkel beheerders, afgedwongen door RLS). */
export const deleteProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => ({ id: String(data.id) }))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("projects").delete().eq("id", data.id);
    if (error) throw new Error(readableError(error.message));
    return { ok: true };
  });

export type ProjectMediaInput = {
  mediaId: string;
  role: ProjectMediaRole;
  pairedMediaId: string | null;
};

/** De volledige fotoreeks van een realisatie opslaan, in de meegegeven volgorde. */
export const setProjectMedia = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { projectId: string; items: ProjectMediaInput[] }) => data)
  .handler(async ({ data, context }) => {
    const { error: clearError } = await context.supabase
      .from("project_media")
      .delete()
      .eq("project_id", data.projectId);
    if (clearError) throw new Error(readableError(clearError.message));

    if (data.items.length === 0) return { ok: true };
    const { error } = await context.supabase.from("project_media").insert(
      data.items.map((item, index) => ({
        project_id: data.projectId,
        media_id: item.mediaId,
        rol: item.role,
        sort_order: index + 1,
        gekoppeld_media_id: item.pairedMediaId,
      })),
    );
    if (error) throw new Error(readableError(error.message));
    return { ok: true };
  });

/** De klantquote van een realisatie bewaren. Zonder toestemming blijft ze onzichtbaar. */
export const saveProjectQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      projectId: string;
      citaat: string;
      naam: string;
      gemeente: string;
      toestemming: boolean;
      toestemmingOp: string | null;
      zichtbaar: boolean;
    }) => data,
  )
  .handler(async ({ data, context }) => {
    const toestemmingOp = data.toestemming ? (data.toestemmingOp ?? new Date().toISOString()) : null;
    const payload = {
      project_id: data.projectId,
      citaat: data.citaat.trim(),
      naam: data.naam.trim(),
      gemeente: data.gemeente.trim(),
      toestemming: data.toestemming,
      toestemming_op: toestemmingOp,
      zichtbaar: data.toestemming && data.zichtbaar,
    };

    const { data: existing, error: readError } = await context.supabase
      .from("project_quotes")
      .select("id")
      .eq("project_id", data.projectId)
      .maybeSingle();
    if (readError) throw new Error(readableError(readError.message));

    if (existing) {
      const { error } = await context.supabase.from("project_quotes").update(payload).eq("id", existing.id);
      if (error) throw new Error(readableError(error.message));
      return { ok: true };
    }
    const { error } = await context.supabase.from("project_quotes").insert(payload);
    if (error) throw new Error(readableError(error.message));
    return { ok: true };
  });

/** Alle gepubliceerde realisaties voor de sitemap. */
export const listProjectsForSitemap = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await publicClient()
    .from("projects")
    .select("slug, updated_at")
    .eq("status", "gepubliceerd");
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => ({ slug: row.slug, lastmod: row.updated_at.slice(0, 10) }));
});
